import Konva from 'konva';
import {
	EDGE_TYPES, LENGTH_VARYING_EDGE_TYPES, INVALID_AREA_THRESHOLD,
	POINT_MATCH_THRESHOLD, POINT_SNAP_THRESHOLD_AFTER_UNMERGE, specialFacetLines
} from './constant';
import type { EdgeType } from './constant';
import { CreateSpecialEdge, LinesData, RoofLine, UpdateSpecialEdge } from 'store/slices/RoofToolLinesSlice/types';
import { isPolygonCCW } from '../utils';
import { validatePolygon, isIntersectionAreaWithInThreshold } from './EditFacet/polygonValidator';

// Euclidean distance from point to line segment
function distanceFromPointToLineSegment(p: { x: number; y: number }, v: { x: number; y: number }, w: { x: number; y: number }) {
	const l2 = (w.x - v.x) ** 2 + (w.y - v.y) ** 2;
	if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
	let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
	t = Math.max(0, Math.min(1, t));
	return Math.hypot(p.x - (v.x + t * (w.x - v.x)), p.y - (v.y + t * (w.y - v.y)));
}

// Distance from point to polygon edge (flattened array of [x1, y1, x2, y2, ...])
function distanceToEdge(point: { x: number; y: number }, polygonPoints: number[]): number {
	let minDistance = Infinity;
	for (let i = 0; i < polygonPoints.length; i += 2) {
		const x1 = polygonPoints[i];
		const y1 = polygonPoints[i + 1];
		const x2 = polygonPoints[(i + 2) % polygonPoints.length];
		const y2 = polygonPoints[(i + 3) % polygonPoints.length];

		const dist = distanceFromPointToLineSegment(point, { x: x1, y: y1 }, { x: x2, y: y2 });
		if (dist < minDistance) minDistance = dist;
	}
	return minDistance;
}

// Check if two points are within match threshold
const arePointsNear = ([x1, y1]: [number, number], [x2, y2]: [number, number], threshold = POINT_MATCH_THRESHOLD) =>
	Math.hypot(x1 - x2, y1 - y2) < threshold;

// Find closest segment to insert point between
function findBetweenPoint(coords: number[][], x: number, y: number): number[][] {
	const click = { x, y };
	let minDist = Infinity;
	let insertIndex = -1;
	let projectedPoint: [number, number] = [x, y];

	for (let i = 0; i < coords.length; i++) {
		const start = coords[i];
		const end = coords[(i + 1) % coords.length];

		const dist = distanceFromPointToLineSegment(click, { x: start[0], y: start[1] }, { x: end[0], y: end[1] });
		if (dist < minDist) {
			minDist = dist;
			insertIndex = i + 1;
			const dx = end[0] - start[0];
			const dy = end[1] - start[1];
			const length = Math.hypot(dx, dy);

			let t = ((x - start[0]) * dx + (y - start[1]) * dy) / (length * length);
			t = Math.max(0, Math.min(1, t));

			projectedPoint = [
				start[0] + t * dx,
				start[1] + t * dy,
			];
		}
	}

	const newCoords = [...coords];
	newCoords.splice(insertIndex, 0, projectedPoint);
	return newCoords;
}

// Used when adding point on line (click)
function addNewPoint(pointerPos: Konva.Vector2d, coordinates: number[][], scale: { x: number; y: number }): number[][] {
	const clickedX = pointerPos.x / scale.x;
	const clickedY = pointerPos.y / scale.y;
	const clickedPoint = roundPoint([clickedX, clickedY]);
	return findBetweenPoint(coordinates, clickedPoint[0], clickedPoint[1]);
}

// Find linked points (same [x,y] in multiple facets). Threshold scales with zoomLevel so
// "same point" always means the same screen distance, like SNAP_RADIUS below.
function findLinkedPoints(
	editableFacets: any[],
	x: number,
	y: number,
	konvaRef: React.RefObject<Konva.Stage>,
	zoomLevel = 1
) {
	const linked: { segmentId: string; pointIndex: number; facetLines: Konva.Line; dotNode: Konva.Circle }[] = [];
	const threshold = POINT_MATCH_THRESHOLD / zoomLevel;

	editableFacets.forEach((facet) => {
		const facetLines = konvaRef?.current?.findOne(`#editable-${facet.segmentId}`) as Konva.Line;
		facet.coordinates.forEach(([px, py]: number[], i: number) => {
			const pointIndex = i * 2;
			if (!arePointsNear([x, y], [px, py], threshold)) return;
			const dotNode = konvaRef?.current?.findOne(`#dot-${facet.segmentId}-${pointIndex}`) as Konva.Circle;

			linked.push({
				segmentId: facet.segmentId,
				pointIndex,
				facetLines,
				dotNode
			});
		});
	});

	return linked;
}

// Checks if current facet has any point overlapping with others
function doesPointsOverlapped(currentFacet: any, otherFacets: any[]) {
	return currentFacet.coordinates.map(([x, y]: [number, number]) => {
		return otherFacets.some(facet =>
			facet.segmentId !== currentFacet.segmentId &&
			facet.coordinates.some(([nx, ny]: [number, number]) => arePointsNear([x, y], [nx, ny]))
		);
	});
}

// Same zoomLevel-scaled threshold as findLinkedPoints, so the "common point" (shared
// corner) highlight only fires when the points are actually close on screen.
function getOverlappingPoints(currentFacet: any, otherFacets: any[], zoomLevel = 1) {
	const threshold = 1 / zoomLevel;
	return currentFacet.coordinates.map(([x, y]: [number, number]) => {
		const overlaps: { [segmentId: string]: Array<[number, number]> } = {};

		for (const facet of otherFacets) {
			if (facet.segmentId === currentFacet.segmentId) continue;

			for (const [nx, ny] of facet.coordinates) {
				if (arePointsNear([x, y], [nx, ny], threshold)) {
					if (!overlaps[facet.segmentId]) {
						overlaps[facet.segmentId] = [[nx, ny]];
					}
					const exists = overlaps[facet.segmentId].some(
						([px, py]) => Math.abs(px - nx) < 0.5 && Math.abs(py - ny) < 0.5
					);
					if (!exists) overlaps[facet.segmentId].push([nx, ny]);
					// overlaps[facet.segmentId].push([nx, ny]);
				}
			}
		}

		return {
			segmentId: currentFacet.segmentId,
			point: [x, y],
			overlappedWith: overlaps
		};
	});
}

type Point = [number, number];
function normalize([x, y]: Point): [number, number] {
	const len = Math.hypot(x, y);
	return len === 0 ? [0, 0] : [x / len, y / len];
}

function moveVertexInward(
	polygon: number[][],
	index: number,
	threshold: number
): number[][] {
	const n = polygon.length;
	const newPolygon: number[][] = polygon.map(p => [...p]); // deep copy

	// treat polygon without the duplicate last vertex for math
	const effectiveN = (polygon[0][0] === polygon[n - 1][0] && polygon[0][1] === polygon[n - 1][1])
		? n - 1
		: n;

	const prev = polygon[(index - 1 + effectiveN) % effectiveN];
	const curr = polygon[index];
	const next = polygon[(index + 1) % effectiveN];

	// edge vectors
	const v1: Point = [curr[0] - prev[0], curr[1] - prev[1]];
	const v2: Point = [next[0] - curr[0], next[1] - curr[1]];

	const isCCW = isPolygonCCW(polygon.flat());

	const n1: Point = isCCW ? normalize([-v1[1], v1[0]]) : normalize([v1[1], -v1[0]]);
	const n2: Point = isCCW ? normalize([-v2[1], v2[0]]) : normalize([v2[1], -v2[0]]);
	const inward: Point = normalize([n1[0] + n2[0], n1[1] + n2[1]]);

	// scale so true offset distance = threshold
	const angleCos = (v1[0] * v2[0] + v1[1] * v2[1]) /
		(Math.hypot(...v1) * Math.hypot(...v2));
	const scale = threshold / Math.max(1, Math.sqrt((1 - angleCos) / 2));

	const moved = roundPoint([
		curr[0] + inward[0] * scale,
		curr[1] + inward[1] * scale
	]);

	// replace only this vertex
	newPolygon[index] = moved;

	// if polygon closes (first == last), also update the matching end
	if (index === 0 && polygon[0][0] === polygon[n - 1][0] && polygon[0][1] === polygon[n - 1][1]) {
		newPolygon[n - 1] = [...moved];
	}
	if (index === n - 1 && polygon[0][0] === polygon[n - 1][0] && polygon[0][1] === polygon[n - 1][1]) {
		newPolygon[0] = [...moved];
	}

	return newPolygon;
}

function injectTouchingPoints(
	facets: {
		segmentId: string;
		coordinates: number[][];
		isEdited: boolean;
		isSelected: boolean;
	}[],
	threshold = 1
) {
	// Step 1: Deep clone facets to work on
	let newFacets = facets.map(f => ({
		...f,
		coordinates: f.coordinates.map(coord => [...coord])
	}));

	// Step 2: splice a vertex into the nearest valid touching edge of every other
	// facet, trying the next-nearest edge if the closest one would self-intersect.
	for (const facet of facets) {
		for (const [x, y] of facet.coordinates) {
			for (const target of newFacets) {
				if (target.segmentId === facet.segmentId) continue;

				const exists = target.coordinates.some(
					([px, py]) => Math.abs(px - x) < 0.5 && Math.abs(py - y) < 0.5
				);
				if (exists) continue;

				const candidateEdges: { edgeIndex: number; dist: number }[] = [];
				for (let i = 0; i < target.coordinates.length; i++) {
					const start = target.coordinates[i];
					const end = target.coordinates[(i + 1) % target.coordinates.length];
					const dist = distanceFromPointToLineSegment(
						{ x, y }, { x: start[0], y: start[1] }, { x: end[0], y: end[1] }
					);
					if (dist < threshold) candidateEdges.push({ edgeIndex: i, dist });
				}
				candidateEdges.sort((a, b) => a.dist - b.dist);

				for (const { edgeIndex } of candidateEdges) {
					const insertIndex = (edgeIndex + 1) % target.coordinates.length;
					const candidate = [...target.coordinates];
					candidate.splice(insertIndex, 0, [x, y]);
					if (!validatePolygon(candidate.flat()).valid) continue;

					target.coordinates = candidate;
					target.isEdited = true;
					break;
				}
			}
		}
	}

	// Step 3: snap near-duplicate points together, but don't merge two non-adjacent
	// points of the same facet — that's usually two real corners, not a duplicate.
	const allPoints: { x: number; y: number; facetIndex: number; pointIndex: number }[] = [];
	newFacets.forEach((facet, facetIndex) => {
		facet.coordinates.forEach(([x, y], pointIndex) => allPoints.push({ x, y, facetIndex, pointIndex }));
	});

	const isRingAdjacent = (facetIndex: number, i: number, j: number) => {
		const len = newFacets[facetIndex].coordinates.length;
		const lo = Math.min(i, j);
		const hi = Math.max(i, j);
		return hi - lo === 1 || (lo === 0 && hi === len - 1);
	};

	const visited = new Array(allPoints.length).fill(false);
	const pointToClusterMap = new Map<string, [number, number]>();

	for (let i = 0; i < allPoints.length; i++) {
		if (visited[i]) continue;

		const cluster = [allPoints[i]];
		visited[i] = true;

		for (let j = i + 1; j < allPoints.length; j++) {
			if (visited[j]) continue;
			const candidate = allPoints[j];

			const collapsesOwnRing = cluster.some(m =>
				m.facetIndex === candidate.facetIndex &&
				!isRingAdjacent(candidate.facetIndex, m.pointIndex, candidate.pointIndex)
			);
			if (collapsesOwnRing) continue;

			if (cluster.some(({ x, y }) => Math.abs(x - candidate.x) < threshold && Math.abs(y - candidate.y) < threshold)) {
				cluster.push(candidate);
				visited[j] = true;
				j = i; // restart — a newly joined point may pull in more neighbours
			}
		}

		const avgX = cluster.reduce((sum, p) => sum + p.x, 0) / cluster.length;
		const avgY = cluster.reduce((sum, p) => sum + p.y, 0) / cluster.length;
		for (const pt of cluster) pointToClusterMap.set(`${pt.facetIndex}-${pt.pointIndex}`, [avgX, avgY]);
	}

	pointToClusterMap.forEach((point, key) => {
		const [facetIndex, pointIndex] = key.split('-').map(Number);
		newFacets[facetIndex].coordinates[pointIndex] = point;
		newFacets[facetIndex].isEdited = true;
	});

	newFacets = newFacets.map(facet => ({
		...facet,
		coordinates: facet.coordinates.filter(
			(pt, idx, arr) => idx === arr.findIndex(([x, y]) => x === pt[0] && y === pt[1])
		)
	}));

	// Safety net: revert a facet to its original shape if it ends up self-intersecting
	// or overlapping another facet. Reverts only what's needed (one facet, not both)
	// so a neighbour's good fix isn't thrown away. Can't fix an overlap that already
	// existed in the input.
	const originalBySegmentId = new Map(facets.map(f => [f.segmentId, f]));
	const revert = (facet: (typeof newFacets)[number]) => {
		const original = originalBySegmentId.get(facet.segmentId);
		return original ? { ...facet, coordinates: original.coordinates.map(c => [...c]), isEdited: false } : facet;
	};

	newFacets = newFacets.map(f => {
		if (validatePolygon(f.coordinates.flat()).valid) return f;
		console.log('injectTouchingPoints: reverted self-intersecting facet', f.segmentId);
		return revert(f);
	});

	// Skip pairs where neither facet changed — reverting an unchanged facet is a
	// no-op, so there's nothing new to check.
	const isUnchanged = (facet: (typeof newFacets)[number]) => {
		const original = originalBySegmentId.get(facet.segmentId);
		if (!original || facet.coordinates.length !== original.coordinates.length) return false;
		return facet.coordinates.every(([x, y], idx) => x === original.coordinates[idx][0] && y === original.coordinates[idx][1]);
	};
	const unchanged = newFacets.map(isUnchanged);

	const overlaps = (fa: (typeof newFacets)[number], fb: (typeof newFacets)[number]) =>
		isIntersectionAreaWithInThreshold(fa.coordinates.flat(), fb.coordinates.flat(), INVALID_AREA_THRESHOLD);

	for (let a = 0; a < newFacets.length; a++) {
		for (let b = a + 1; b < newFacets.length; b++) {
			if (unchanged[a] && unchanged[b]) continue;
			if (!newFacets[a].coordinates.length || !newFacets[b].coordinates.length) continue;
			if (!overlaps(newFacets[a], newFacets[b])) continue;

			// Try reverting just A, then just B, then both as a last resort.
			const revertedA = revert(newFacets[a]);
			const revertedB = revert(newFacets[b]);
			let which = 'both';
			let fa = revertedA, fb = revertedB;
			if (!overlaps(revertedA, newFacets[b])) {
				which = 'A'; fa = revertedA; fb = newFacets[b];
			} else if (!overlaps(newFacets[a], revertedB)) {
				which = 'B'; fa = newFacets[a]; fb = revertedB;
			}

			console.log(`injectTouchingPoints: reverted ${which} to resolve overlap`, newFacets[a].segmentId, newFacets[b].segmentId);
			newFacets[a] = fa;
			newFacets[b] = fb;
		}
	}

	return newFacets;
}

const normalizeCoordinatesString = (coord: [number, number][]): string => {
	const [p1, p2] = coord;
	const [x1, y1] = p1;
	const [x2, y2] = p2;

	const pointA = { x: x1, y: y1 };
	const pointB = { x: x2, y: y2 };

	let start, end;
	if (pointA.x < pointB.x || (pointA.x === pointB.x && pointA.y <= pointB.y)) {
		start = pointA;
		end = pointB;
	} else {
		start = pointB;
		end = pointA;
	}

	return `${start.x}-${start.y}_${end.x}-${end.y}`;
};

const getMousePositionLines = (e: Konva.KonvaEventObject<PointerEvent>, stoke: number) => {
	const stage = e.target.getStage();
	const pointerPos = stage?.getPointerPosition();

	const shapeResult: { edgeLines: Konva.Line[], gutterLines: Konva.Line[]} = {
		edgeLines: [],
		gutterLines: []
	};

	if (!pointerPos || !stage) return shapeResult;

	// Use Konva’s hit detection first
	const intersectedShapes = stage.getAllIntersections(pointerPos);

	const hoveredLines = [];

	for(const shape of intersectedShapes){
		if(!(shape instanceof Konva.Line)){
			continue;
		}

		const name = shape.attrs.name;
		if(!name) continue;
		if(name.includes('edge-lines')){
			hoveredLines.push(shape);
		}else if(name.startsWith('gutter-line')){
			shapeResult.gutterLines.push(shape);
		}
	}
	// const hoveredLines = intersectedShapes.filter(
	// 	(each) => typeof each.attrs.name === 'string' && each.attrs.name.includes('edge-lines')
	// );

	if (hoveredLines.length === 0) return shapeResult;

	// Compute distances manually to find the closest line(s)
	const shapesWithDistance = hoveredLines.map((line) => {
		const transform = line.getAbsoluteTransform().copy().invert();
		const local = transform.point(pointerPos);

		const points: number[] = line.attrs.points;
		let minDist = Infinity;

		for (let i = 0; i < points.length - 2; i += 2) {
			const v = { x: points[i], y: points[i + 1] };
			const w = { x: points[i + 2], y: points[i + 3] };
			const dist = distanceFromPointToLineSegment(local, v, w);
			if (dist < minDist) minDist = dist;
		}
		return { shape: line, distance: minDist };
	});

	// Sort by distance
	shapesWithDistance.sort((a, b) => a.distance - b.distance);

	// Always return the closest line
	shapeResult.edgeLines = [shapesWithDistance[0].shape];

	// Also return the second one if it's nearly overlapping
	if (
		shapesWithDistance.length > 1 &&
		shapesWithDistance[1].distance - shapesWithDistance[0].distance < 3 // overlap threshold
	) {
		shapeResult.edgeLines.push(shapesWithDistance[1].shape);
	}

	return shapeResult;
};

function areEdgesEqual(edge1: number[][], edge2: number[][]) {
	const sortEdgeCoords = (edge: number[][]) => {
		const [p1, p2] = edge;
		return [p1, p2].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
	};
	const [e1p1, e1p2] = sortEdgeCoords(edge1);
	const [e2p1, e2p2] = sortEdgeCoords(edge2);
	return e1p1[0] === e2p1[0] && e1p1[1] === e2p1[1] &&
		e1p2[0] === e2p2[0] && e1p2[1] === e2p2[1];
}

// an edge which is not classified as any of the special edge types yet
function isUntypedEdge(lineType?: string | null) {
	return !lineType || lineType === EDGE_TYPES.NONE;
}

// edge types come off the api / redux state as plain strings, so narrow them here
// instead of casting at every call site
function isSpecialFacetLine(lineType?: string | null): lineType is EdgeType {
	return (specialFacetLines as readonly string[]).includes(lineType ?? '');
}

function isLengthVaryingEdge(edgeType?: string | null): edgeType is EdgeType {
	return (LENGTH_VARYING_EDGE_TYPES as readonly string[]).includes(edgeType ?? '');
}

// builds the create/update special-edge payloads for the given edges and edge type
function buildSpecialEdgePayload(
	updateLines: Array<{ facetId: string; id: number; discard?: boolean }>,
	lineType: string,
	existingSpecialEdges: SpecialEdges[]
) {
	const selectedKeys = updateLines.map(({ facetId, id }) => `${facetId}:${id}`);

	const existingSpecialEdgeIndex = existingSpecialEdges.findIndex(({ edges }) => {
		const existingSet = new Set(
			Object.entries(edges).map(([facetId, edgeId]) => `${facetId}:${edgeId}`)
		);
		return selectedKeys.some(eachKey => existingSet.has(eachKey));
	});

	const edges = updateLines.map(({ facetId, id, discard }) => ({
		roofSegmentId: facetId,
		roofSegmentEdgeId: id,
		discard,
	}));

	if (existingSpecialEdgeIndex !== -1) {
		return {
			createSpecialEdgesData: {} as CreateSpecialEdge,
			updateSpecialEdgesData: {
				specialEdgeId: existingSpecialEdges[existingSpecialEdgeIndex].id,
				edgeType: lineType,
				edges,
			} as UpdateSpecialEdge,
		};
	}

	return {
		createSpecialEdgesData: { edgeType: lineType, edges } as CreateSpecialEdge,
		updateSpecialEdgesData: {} as UpdateSpecialEdge,
	};
}

function doesSpecialEdgeOverlap<T extends CreateSpecialEdge | UpdateSpecialEdge>(specialEdges: T[]) {
	return specialEdges.find(eachLine => eachLine.edges.every(everyEdge => everyEdge.discard));
}

function doesGutterLinesOverlap(facets: LinesData['selectedFacets']) {
	return Object.keys(facets).some(eachFacetKey => {
		const currentFacet = facets[eachFacetKey];
		return currentFacet.newAddedGutterLines.some(eachLine => eachLine.discard);
	});
}

function updateDiscardFlag<T extends CreateSpecialEdge | UpdateSpecialEdge>(
	edgeData: T | undefined,
	segmentId: string
): T | undefined {
	if (!edgeData) return edgeData;

	return {
		...edgeData,
		edges: edgeData.edges.map(eachEdge =>
			eachEdge.roofSegmentId === segmentId
				? { ...eachEdge, discard: false }
				: eachEdge
		),
	};
}

function roundPoint([x, y]: number[], precision = 3): number[] {
	// return [x, y];
	const factor = Math.pow(10, precision);
	return [Math.round(x * factor) / factor, Math.round(y * factor) / factor];
}

function snapNearbyPointsToTargetPoint(facetPoints: number[][], targetPointIndex: number) {
	let [mx, my] = roundPoint(facetPoints[targetPointIndex]);

	// check against all other points
	const mergedCoords = facetPoints.map(([x, y], i) => {
		if (i === targetPointIndex) return roundPoint([mx, my]); // skip itself

		const dist = Math.hypot(mx - x, my - y);
		if (dist <= POINT_SNAP_THRESHOLD_AFTER_UNMERGE) {
			const midX = (mx + x) / 2;
			const midY = (my + y) / 2;
			mx = midX;
			my = midY;
			return roundPoint([midX, midY]); // replace other point with midpoint
		}
		return roundPoint([x, y]);
	});

	mergedCoords[targetPointIndex] = [mx, my];

	// remove exact duplicates left after merging
	facetPoints = mergedCoords.filter(
		(pt, i, arr) =>
			i === arr.findIndex(([px, py]) => px === pt[0] && py === pt[1])
	);
	return facetPoints;
}

export {
	findBetweenPoint,
	arePointsNear,
	findLinkedPoints,
	addNewPoint,
	distanceToEdge,
	doesPointsOverlapped,
	injectTouchingPoints,
	normalizeCoordinatesString,
	getMousePositionLines,
	areEdgesEqual,
	isUntypedEdge,
	isSpecialFacetLine,
	isLengthVaryingEdge,
	buildSpecialEdgePayload,
	doesSpecialEdgeOverlap,
	doesGutterLinesOverlap,
	updateDiscardFlag,
	getOverlappingPoints,
	moveVertexInward,
	roundPoint,
	snapNearbyPointsToTargetPoint
};
